import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { designSystem } from '@/styles/theme';
import { claudeTokens } from '@/styles/claudeTokens';

/**
 * 공통 로딩 인디케이터 — 헤더 로고(명치사.)가 그라데이션으로 반짝이는 브랜드 로더.
 * 페이지/섹션 로딩 상태는 전부 이 컴포넌트로 통일한다. (문구·MUI 스피너 혼용 금지)
 */

// 글자 위를 청록 하이라이트가 좌→우로 쓸고 지나가는 효과
const shimmer = keyframes`
  0% { background-position: 130% 0; }
  100% { background-position: -130% 0; }
`;

const dotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.55); opacity: 0.45; }
`;

const Wrapper = styled.div<{ minHeightValue: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: ${({ minHeightValue }) => minHeightValue};
`;

// app-content는 뒤로가기/당겨 새로고침 중 transform된다. body 포털에서 그리면
// 페이지의 여백·스크롤·transform과 관계없이 항상 뷰포트 중앙에 머문다.
const PageIndicator = styled.div`
  display: flex;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${designSystem.zIndex.sticky - 1};
  pointer-events: none;
`;

const PagePlaceholder = styled.div<{ topOffset: number }>`
  width: 100%;
  min-height: max(0px, calc(100vh - ${({ topOffset }) => topOffset}px));
  min-height: max(0px, calc(100dvh - ${({ topOffset }) => topOffset}px));
  background: ${claudeTokens.color.surface};
`;

/** 헤더·탭·필터 아래 남은 화면을 채운다. 본문의 제스처 transform은 높이에 포함하지 않는다. */
const PageLoading: FC<{ children: ReactNode }> = ({ children }) => {
    const placeholderRef = useRef<HTMLDivElement>(null);
    const [topOffset, setTopOffset] = useState(0);

    useLayoutEffect(() => {
        const placeholder = placeholderRef.current;
        if (!placeholder) return;
        const measure = () => {
            let top = 0;
            let element: HTMLElement | null = placeholder;
            while (element) {
                top += element.offsetTop;
                element = element.offsetParent as HTMLElement | null;
            }
            setTopOffset(top);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        const header = document.querySelector('header');
        if (header) observer?.observe(header);
        // 필터 줄·배너 등 앞선 형제의 높이가 바뀌어도 시작 위치를 다시 잰다.
        let parent = placeholder.parentElement;
        while (parent) {
            observer?.observe(parent);
            if (parent.id === 'app-content') break;
            parent = parent.parentElement;
        }
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    return (
        <>
            <PagePlaceholder ref={placeholderRef} topOffset={topOffset} aria-hidden="true" data-page-loading-space="" />
            {createPortal(
                <PageIndicator role="status" aria-label="로딩 중" data-loading-placement="page">
                    {children}
                </PageIndicator>,
                document.body,
            )}
        </>
    );
};

const Wordmark = styled.span`
  display: inline-flex;
  align-items: baseline;
  font-family: 'Pretendard Variable', Pretendard, system-ui, sans-serif;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  user-select: none;
`;

const ShimmerText = styled.span`
  background: linear-gradient(
    90deg,
    ${claudeTokens.color.text1} 0%,
    ${claudeTokens.color.text1} 32%,
    #5a9b96 50%,
    ${claudeTokens.color.text1} 68%,
    ${claudeTokens.color.text1} 100%
  );
  background-size: 240% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${shimmer} 1.7s ease-in-out infinite;
`;

const Dot = styled.span`
  display: inline-block;
  width: 0.32em;
  height: 0.32em;
  margin-left: 0.08em;
  background: #5a9b96;
  border-radius: 50%;
  animation: ${dotPulse} 1.7s ease-in-out infinite;
`;

/**
 * 부분 로더는 요청한 높이 안에서 가운데 정렬한다.
 * 페이지 로더는 페이지마다 고정 높이를 지정하지 않고 남은 뷰포트를 채운다.
 */
export const PAGE_LOADER_MIN_HEIGHT: string = '60vh';

interface LoadingSpinnerProps {
    /** page: 화면 진입/주 데이터 조회. inline: 더보기·부분 영역·모달 내부 조회. */
    placement?: 'page' | 'inline';
    /** inline 모드의 영역 높이. page 모드는 현재 위치부터 뷰포트 하단까지 자동으로 채운다. */
    minHeight?: string;
}

const LoadingSpinner: FC<LoadingSpinnerProps> = ({ minHeight = PAGE_LOADER_MIN_HEIGHT, placement = 'inline' }) => {
    const wordmark = (
        <Wordmark>
            <ShimmerText>명치사</ShimmerText>
            <Dot />
        </Wordmark>
    );

    if (placement === 'page') {
        return <PageLoading>{wordmark}</PageLoading>;
    }

    return (
        <Wrapper minHeightValue={minHeight} role="status" aria-label="로딩 중" data-loading-placement="inline">
            {wordmark}
        </Wrapper>
    );
};

export default LoadingSpinner;
